import { useTranslation } from "react-i18next";
import { Clock, Mail, MessageCircle, Phone } from "lucide-react";
import { CONTACTS } from "src/config/contacts";
import { genericWhatsappHref } from "src/utils/whatsapp";
import Faq from "src/sections/faq/faq";
import Footer from "src/sections/components/footer/footer";
import PageHero from "src/sections/components/page-hero";
import PatternLayer from "src/sections/components/pattern-layer";
import PhotoBand from "src/sections/components/photo-band";
import SakuraDivider from "src/sections/components/sakura-divider";
import { Instagram } from "src/sections/assets/icons/icons";
import { Telegram, Whatsapp, Youtube } from "src/sections/assets/icons/social-media";
import { usePageMeta } from "src/utils/seo";

// СТРАНИЦА «КОНТАКТЫ И FAQ».
//
// Сюда человек приходит с конкретным намерением: написать. Поэтому первое,
// что он видит, — способы связи, а не рассказ о компании. Частые вопросы
// стоят ниже той же секцией, что и на главной (компонент один, тексты одни:
// расхождений между страницами быть не может).
//
// Пустые значения в src/config/contacts.js убирают свою карточку — если у
// компании нет YouTube, пустой плитки не появится.

const CARD =
  "relative overflow-hidden rounded-2xl border border-kj-line bg-kj-card/50 p-5 sm:p-6 transition-colors hover:border-kj-gold/60";

export default function ContactsPage() {
  const { t } = useTranslation();

  usePageMeta({
    title: t("contactsPage.title"),
    description: t("contactsPage.subtitle"),
  });

  const socials = [
    { name: "WhatsApp", href: CONTACTS.whatsapp, Icon: Whatsapp },
    { name: "Telegram", href: CONTACTS.telegram, Icon: Telegram },
    { name: "Instagram", href: CONTACTS.instagram, Icon: Instagram },
    { name: "YouTube", href: CONTACTS.youtube, Icon: Youtube },
  ].filter((s) => s.href);

  // Телефон и почта — ссылками: на телефоне номер набирается в одно касание.
  const lines = [
    CONTACTS.phone && {
      id: "phone",
      Icon: Phone,
      label: t("contactsPage.phoneLabel"),
      value: CONTACTS.phone,
      href: `tel:${CONTACTS.phone.replace(/[^\d+]/g, "")}`,
    },
    CONTACTS.email && {
      id: "email",
      Icon: Mail,
      label: t("contactsPage.emailLabel"),
      value: CONTACTS.email,
      href: `mailto:${CONTACTS.email}`,
    },
  ].filter(Boolean);

  return (
    <div className="min-h-screen w-full flex flex-col bg-kj-dark text-kj-ink">
      <PageHero
        eyebrow={t("contactsPage.eyebrow")}
        title={t("contactsPage.title")}
        subtitle={t("contactsPage.subtitle")}
      />

      <div className="relative">
        <PhotoBand mirrored className="top-24 h-[440px] sm:h-[560px]" />

      <main className="relative z-10 max-w-[1526px] w-full mx-auto px-4 mt-10 sm:mt-14 mb-12 flex flex-col gap-y-12 sm:gap-y-16">
        <section className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:gap-10">
          <div>
            <h2 className="text-xl sm:text-2xl font-extrabold uppercase tracking-tight text-kj-ink">
              {t("contactsPage.writeTitle")}
            </h2>
            <p className="mt-3 text-base text-kj-sub leading-relaxed max-w-[48ch]">
              {t("contactsPage.writeText")}
            </p>

            {CONTACTS.whatsapp && (
              <a
                href={genericWhatsappHref(t)}
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex min-h-[48px] items-center gap-2 rounded-sm bg-kj-gold px-7 text-sm font-bold uppercase tracking-[0.08em] text-kj-dark transition-colors hover:bg-kj-gold/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold focus-visible:ring-offset-2 focus-visible:ring-offset-kj-dark"
              >
                <MessageCircle aria-hidden="true" className="h-4 w-4" />
                {t("footer.writeWhatsapp")}
              </a>
            )}

          </div>

          <div className="grid gap-4 sm:grid-cols-2 self-start">
            {lines.map(({ id, Icon, label, value, href }) => (
              <a key={id} href={href} className={CARD}>
                <PatternLayer kind="asanoha" />
                <span className="relative flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-kj-gold">
                  <Icon aria-hidden="true" className="h-3.5 w-3.5" />
                  {label}
                </span>
                <span className="relative mt-2 block break-all text-base sm:text-lg font-bold text-kj-ink">
                  {value}
                </span>
              </a>
            ))}

            <div className={`${CARD} sm:col-span-2`}>
              <PatternLayer kind="asanoha" />
              <span className="relative flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-kj-gold">
                <Clock aria-hidden="true" className="h-3.5 w-3.5" />
                {t("contactsPage.hoursTitle")}
              </span>
              <p className="relative mt-2 text-sm sm:text-base text-kj-sub leading-relaxed">
                {t("contactsPage.hoursText")}
              </p>
            </div>

            {socials.length > 0 && (
              <div className={`${CARD} sm:col-span-2`}>
                <PatternLayer kind="asanoha" />
                <span className="relative text-xs font-bold uppercase tracking-[0.14em] text-kj-gold">
                  {t("contactsPage.socialLabel")}
                </span>
                <div className="relative mt-3 flex flex-wrap items-center gap-3">
                  {socials.map(({ name, href, Icon }) => (
                    <a
                      key={name}
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      className="flex min-h-[44px] items-center gap-2 rounded-lg border border-kj-line px-4 text-sm font-semibold text-kj-sub transition-colors hover:border-kj-gold hover:text-kj-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold"
                    >
                      <Icon className="h-5 w-5" aria-hidden="true" />
                      {name}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        <SakuraDivider variant="right" season="momiji" className="!px-0" />
      </main>
      </div>

      {/* Те же вопросы и ответы, что на главной: один компонент, один текст. */}
      <div className="mb-14">
        <Faq />
      </div>

      <Footer />
    </div>
  );
}
